import type { EdiAnchor } from "@/api/mapperApi";
import { EdiVerortung } from "./EdiVerortung";

// Keep the transform color values in sync with BridgeRow.tsx (TF_COLOR).
const TF_COLOR: Record<string, string> = {
  identity: "var(--sapNeutralColor, #6a6d70)",
  code_lookup: "var(--sapInformativeColor, #0071e3)",
  date_format: "var(--sapPositiveColor, #34c759)",
};

const LABEL = "var(--sapContent_LabelColor)";
const MONO = "var(--sapFontMonoFamily, monospace)";
const BORDER = "var(--sapList_BorderColor)";
const RADIUS = "var(--sapElement_BorderCornerRadius, 8px)";

function directionText(direction: string): string {
  if (direction === "inbound") return "EDI → BO4E";
  if (direction === "outbound") return "BO4E → EDI";
  return "beide";
}

function zoneStyle(active: boolean): React.CSSProperties {
  return {
    cursor: "pointer",
    padding: "8px 10px",
    borderRadius: RADIUS,
    border: `1px solid ${active ? "var(--sapContent_FocusColor, var(--sapList_BorderColor))" : "transparent"}`,
    background: active ? "var(--sapList_SelectionBackgroundColor, rgba(0,113,227,0.08))" : "transparent",
    minWidth: 0,
  };
}

export function MappingFlowHeader({
  anchor,
  transformLabel,
  transformType,
  bo4e,
  bo4eType,
  bo4eDescription,
  direction,
  step,
  onJump,
}: {
  anchor: EdiAnchor | null;
  transformLabel: string;
  transformType: string;
  bo4e: { bo: string; path: string };
  bo4eType?: string;
  bo4eDescription?: string | null;
  direction: string;
  step: number;
  onJump: (step: number) => void;
}): JSX.Element {
  const pillColor = TF_COLOR[transformType] ?? "var(--sapNeutralColor,#6a6d70)";

  return (
    <div
      data-testid="mapping-flow-header"
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0,1fr) auto minmax(0,1fr)",
        alignItems: "stretch",
        gap: "8px",
        padding: "8px",
        marginBottom: "10px",
        background: "var(--sapList_Background)",
        border: `1px solid ${BORDER}`,
        borderRadius: RADIUS,
      }}
    >
      {/* Left: EDI source */}
      <div
        data-testid="flow-zone-edi"
        role="button"
        onClick={() => onJump(1)}
        style={zoneStyle(step === 1)}
      >
        <div style={{ fontSize: "0.68rem", color: LABEL, marginBottom: "4px" }}>EDI-Quelle</div>
        {anchor ? (
          <EdiVerortung anchor={anchor} align="left" />
        ) : (
          <div style={{ color: LABEL, opacity: 0.6, fontStyle: "italic", fontSize: "0.8rem" }}>
            EDI-Quelle wählen
          </div>
        )}
      </div>

      {/* Middle: Transform */}
      <div
        data-testid="flow-zone-transform"
        role="button"
        onClick={() => onJump(3)}
        style={{
          ...zoneStyle(step === 3),
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          gap: "3px",
        }}
      >
        <div style={{ fontSize: "1.1rem", color: LABEL, lineHeight: 1 }} aria-hidden>
          →
        </div>
        <span
          style={{
            whiteSpace: "nowrap",
            padding: "2px 8px",
            borderRadius: "999px",
            fontSize: "0.68rem",
            fontWeight: 700,
            color: "#fff",
            background: pillColor,
          }}
        >
          {transformType}
        </span>
        {transformLabel && (
          <div style={{ fontSize: "0.7rem", color: LABEL }}>{transformLabel}</div>
        )}
        <div style={{ fontSize: "0.68rem", color: LABEL, opacity: 0.85 }}>
          {directionText(direction)}
        </div>
      </div>

      {/* Right: BO4E target */}
      <div
        data-testid="flow-zone-bo4e"
        role="button"
        onClick={() => onJump(2)}
        style={{ ...zoneStyle(step === 2), textAlign: "right" }}
      >
        <div style={{ fontSize: "0.68rem", color: LABEL, marginBottom: "4px" }}>BO4E-Ziel</div>
        <div style={{ fontFamily: MONO, fontSize: "0.82rem", fontWeight: 600 }}>
          {bo4e.bo + "." + bo4e.path}
        </div>
        {bo4eType && (
          <div style={{ fontSize: "0.72rem", color: LABEL }}>Typ: {bo4eType}</div>
        )}
        {bo4eDescription && (
          <div style={{ fontSize: "0.72rem", color: LABEL, marginTop: "2px" }}>
            {bo4eDescription}
          </div>
        )}
      </div>
    </div>
  );
}
